import { FC, useMemo, useRef, useState } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DraggableProvided,
  DraggableRubric,
  DraggableStateSnapshot,
} from "@hello-pangea/dnd";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Table,
  TableHeader,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
} from "@capacit-com/lara-law-shared-ui";
import { useNavigate } from "react-router-dom";
import useUsers from "@/hooks/useUsers";
import AppRoutes from "@/constants/routes/AppRoutes";
import { openAppNavigation } from "@/lib/navigation/appNavigation";
import { useWorkspaceDocumentVersionStore } from "@/stores/workspace-document-version.store";
import FavoriteButton from "@/components/buttons/FavoriteButton";
import { DocumentCategory } from "@/models/legislation/Legislation";
import { FavoriteDocumentsWidgetConfig } from "@/models/dashboard/WidgetConfig";
import { Skeleton } from "@capacit-com/lara-law-shared-ui";
import { FileHeart, GripVertical, MoreVertical, Pencil } from "lucide-react";
import { UsersFavoriteDocuments } from "@/models/users/User";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
import RenameFavoriteDialog from "../components/RenameFavoriteDialog";
import {
  buildFavoriteDocumentItems,
  buildFullDisplayOrderForGlobalUpdate,
  FavoriteDocumentItem,
  filterFavoriteDocuments,
  resolveItemOrder,
} from "../lib/resolveItemOrder";

/** Consolidation progress badges need favorites API metadata — see consolidationDashboardSpike.ts */

interface FavoriteDocumentsWidgetProps {
  widgetId: string;
  name: string;
  config: FavoriteDocumentsWidgetConfig;
  isEditMode: boolean;
  onConfigUpdate?: (config: FavoriteDocumentsWidgetConfig) => void;
}

const FavoriteDocumentsWidget: FC<FavoriteDocumentsWidgetProps> = ({
  widgetId: _widgetId,
  name: _name,
  config,
  isEditMode,
  onConfigUpdate,
}) => {
  const {
    useQueryUsersFavoriteDocuments,
    renameFavoriteLegislation,
    renameFavoriteWorkspaceDocument,
  } = useUsers();
  const usersFavoriteDocuments = useQueryUsersFavoriteDocuments();
  const navigate = useNavigate();
  const [favoriteToRename, setFavoriteToRename] =
    useState<FavoriteDocumentItem | null>(null);

  const favoriteDocuments = useMemo<UsersFavoriteDocuments>(
    () =>
      usersFavoriteDocuments.data ?? {
        favoriteLegislations: [],
        favoriteWorkspaceDocuments: [],
        displayOrder: [],
      },
    [usersFavoriteDocuments.data]
  );

  const { resetWorkspaceDocumentVersionStore } =
    useWorkspaceDocumentVersionStore();
  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const cloneColumnWidthsRef = useRef<number[]>([]);
  const sourceTableWidthRef = useRef<number | null>(null);

  const dataSource = config.dataSource ?? "all";
  const showTypeColumn = dataSource === "all";

  const getListRowAtIndex = (index: number) =>
    widgetContainerRef.current?.querySelectorAll(
      '[data-slot="table"] tbody tr'
    )[index] as HTMLElement | undefined;

  const getSourceTableElement = () =>
    widgetContainerRef.current?.querySelector(
      '[data-slot="table"]'
    ) as HTMLElement | undefined;

  const measureCloneLayout = (sourceIndex: number) => {
    const sourceRow = getListRowAtIndex(sourceIndex);
    const sourceTable = getSourceTableElement();
    const rowCellWidths = sourceRow
      ? Array.from(sourceRow.querySelectorAll("td")).map(
          (cell) => cell.getBoundingClientRect().width
        )
      : [];
    const headerCellWidths = Array.from(
      sourceTable?.querySelectorAll("thead th") ?? []
    ).map((cell) => cell.getBoundingClientRect().width);

    const measuredColumnWidths =
      rowCellWidths.length > 0 ? rowCellWidths : headerCellWidths;
    const columnWidths =
      measuredColumnWidths.length > 0
        ? measuredColumnWidths
        : cloneColumnWidthsRef.current;

    if (measuredColumnWidths.length > 0) {
      cloneColumnWidthsRef.current = measuredColumnWidths;
    }

    const tableWidth =
      sourceTable?.getBoundingClientRect().width ??
      widgetContainerRef.current?.clientWidth ??
      null;

    if (tableWidth) {
      sourceTableWidthRef.current = tableWidth;
    }

    return {
      columnWidths,
      tableWidth: sourceTableWidthRef.current ?? tableWidth,
    };
  };

  const handleNavigate = (
    document: FavoriteDocumentItem["document"],
    type: "legislation" | "workspace" | "workspaceDocument",
    event?: React.MouseEvent
  ) => {
    if (
      type === "legislation" &&
      "internalId" in document &&
      "datasource" in document
    ) {
      openAppNavigation({
        target: {
          kind: "legislation",
          documentId: document.externalId,
          source: document.datasource,
          context: "app",
        },
        navigate,
        event,
      });
    } else if (
      type === "workspaceDocument" &&
      "workspaceId" in document &&
      "documentId" in document
    ) {
      openAppNavigation({
        target: {
          kind: "workspaceDocument",
          workspaceId: document.workspaceId,
          documentId: document.documentId,
        },
        navigate,
        event,
        onWorkspaceDocumentNavigate: resetWorkspaceDocumentVersionStore,
      });
    } else if (type === "workspace" && "workspaceId" in document) {
      openAppNavigation({
        target: {
          kind: "path",
          path: AppRoutes.workspace(document.workspaceId),
        },
        navigate,
        event,
      });
    }
  };

  /**
   * Renames the favorite itself — the user's own label on it. The legislation source or
   * workspace document behind it keeps its own title (LAR-418).
   */
  const handleRenameFavorite = async (title: string) => {
    const item = favoriteToRename;
    if (!item) return;

    try {
      if (item.type === "legislation") {
        await renameFavoriteLegislation.mutateAsync({
          externalId: item.document.externalId,
          title,
        });
      } else {
        await renameFavoriteWorkspaceDocument.mutateAsync({
          documentId: item.document.documentId,
          title,
        });
      }
      showSuccessToast("Favoritten er omdøbt");
    } catch (error) {
      logger.error("Failed to rename favorite", {
        operation: "renameFavorite",
        error,
        favoriteType: item.type,
        favoriteId: item.id,
      });
      showErrorToast("Kunne ikke omdøbe favoritten. Prøv igen.");
      // Rethrown so the dialog stays open with the typed name intact.
      throw error;
    }
  };

  const allDocumentItems = useMemo(
    () =>
      buildFavoriteDocumentItems(
        favoriteDocuments.favoriteLegislations,
        favoriteDocuments.favoriteWorkspaceDocuments
      ),
    [favoriteDocuments.favoriteLegislations, favoriteDocuments.favoriteWorkspaceDocuments]
  );

  const favoriteDocumentItems = useMemo(() => {
    const filtered = filterFavoriteDocuments(allDocumentItems, dataSource);
    return resolveItemOrder(filtered, {
      globalOrder: favoriteDocuments.displayOrder,
      itemOrder: config.itemOrder,
      useGlobalOrder: config.useGlobalOrder,
    });
  }, [allDocumentItems, favoriteDocuments.displayOrder, dataSource, config.itemOrder, config.useGlobalOrder]);

  const handleDragEnd = (result: DropResult) => {
    cloneColumnWidthsRef.current = [];
    sourceTableWidthRef.current = null;

    if (!isEditMode || !onConfigUpdate || !result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    if (sourceIndex === destinationIndex) return;

    const reordered = [...favoriteDocumentItems];
    const [removed] = reordered.splice(sourceIndex, 1);
    reordered.splice(destinationIndex, 0, removed);

    const allIds = allDocumentItems.map((item) => item.id);
    const itemOrder = buildFullDisplayOrderForGlobalUpdate(
      reordered.map((item) => item.id),
      allIds,
      config.itemOrder ?? favoriteDocuments.displayOrder
    );

    onConfigUpdate({
      ...config,
      useGlobalOrder: false,
      itemOrder,
    });
  };

  const renderFavoriteMenu = (item: FavoriteDocumentItem) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-6"
          aria-label="Handlinger for favorit"
          data-testid="favorite-actions-trigger"
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => setFavoriteToRename(item)}>
          <Pencil className="h-4 w-4 mr-2" />
          Omdøb
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const renderDocumentRow = (
    item: FavoriteDocumentItem,
    draggableProvided: DraggableProvided,
    snapshot: DraggableStateSnapshot
  ) => {
    const { style: dragStyle, ...draggableRowProps } =
      draggableProvided.draggableProps;

    return (
      <TableRow
        ref={draggableProvided.innerRef}
        {...draggableRowProps}
        style={
          dragStyle as React.ComponentProps<typeof TableRow>["style"]
        }
        className={`${snapshot.isDragging ? "bg-muted/40 shadow-md" : "h-10"}`}
      >
        {isEditMode && (
          <TableCell className="w-8 p-0">
            <div
              className="flex items-center justify-center cursor-grab active:cursor-grabbing"
              {...draggableProvided.dragHandleProps}
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
          </TableCell>
        )}
        {showTypeColumn && (
          <TableCell
            className="cursor-pointer hover:text-primary pl-3"
            title={
              item.type === "workspaceDocument" ? "Gå til workspace" : undefined
            }
            onClick={(e) =>
              item.type === "legislation"
                ? handleNavigate(item.document, "legislation", e)
                : handleNavigate(item.document, "workspace", e)
            }
          >
            {item.type === "legislation" ? "Kilder" : "Workspace"}
          </TableCell>
        )}
        <TableCell
          className={`max-w-[300px] cursor-pointer hover:text-primary ${!showTypeColumn ? "pl-3" : ""}`}
          onClick={(e) =>
            item.type === "legislation"
              ? handleNavigate(item.document, "legislation", e)
              : handleNavigate(item.document, "workspaceDocument", e)
          }
        >
          <div className="truncate">{item.document.title}</div>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-0.5">
            {item.type === "legislation" ? (
              <FavoriteButton
                isHovering={false}
                documentCategory={DocumentCategory.Legislation}
                documentId={item.document.externalId}
                title={item.document.title}
                datasource={item.document.datasource}
                internalId={item.document.internalId}
                isFavorite={true}
                className="size-6"
              />
            ) : (
              <FavoriteButton
                isHovering={false}
                documentCategory={DocumentCategory.Workspace}
                workspaceId={item.document.workspaceId}
                documentId={item.document.documentId}
                title={item.document.title}
                isFavorite={true}
                className="size-6"
              />
            )}
            {renderFavoriteMenu(item)}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  const renderCloneDocumentRow = (
    item: FavoriteDocumentItem,
    dragHandleProps: DraggableProvided["dragHandleProps"]
  ) => (
    <TableRow className="h-10 border-b bg-background hover:bg-background">
      {isEditMode && (
        <TableCell className="w-8 p-0">
          <div
            className="flex items-center justify-center cursor-grabbing text-muted-foreground"
            {...dragHandleProps}
          >
            <GripVertical className="h-4 w-4" />
          </div>
        </TableCell>
      )}
      {showTypeColumn && (
        <TableCell className="pl-3">
          {item.type === "legislation" ? "Kilder" : "Workspace"}
        </TableCell>
      )}
      <TableCell className={`max-w-[300px] ${!showTypeColumn ? "pl-3" : ""}`}>
        <div className="truncate">{item.document.title}</div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-0.5">
          {item.type === "legislation" ? (
            <FavoriteButton
              isHovering={false}
              documentCategory={DocumentCategory.Legislation}
              documentId={item.document.externalId}
              title={item.document.title}
              datasource={item.document.datasource}
              internalId={item.document.internalId}
              isFavorite={true}
              className="size-6"
            />
          ) : (
            <FavoriteButton
              isHovering={false}
              documentCategory={DocumentCategory.Workspace}
              workspaceId={item.document.workspaceId}
              documentId={item.document.documentId}
              title={item.document.title}
              isFavorite={true}
              className="size-6"
            />
          )}
          {renderFavoriteMenu(item)}
        </div>
      </TableCell>
    </TableRow>
  );

  const emptyMessage =
    dataSource === "sources"
      ? "Ingen favoriserede kilder"
      : dataSource === "workspace-documents"
        ? "Ingen favoriserede workspace-dokumenter"
        : "Ingen favoriserede dokumenter";

  if (usersFavoriteDocuments.isPending) {
    return (
      <div className="space-y-2 p-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (favoriteDocumentItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 space-y-3 text-muted-foreground">
        <FileHeart className="h-8 w-8" />
        <p className="text-sm">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div ref={widgetContainerRef} className="themed-scrollbar h-full overflow-auto">
      {isEditMode && (
        <p className="text-xs text-muted-foreground px-3 py-1 border-b border-border">
          Træk for at ændre rækkefølge — gem dashboard. Brug widget-indstillinger
          for global rækkefølge.
        </p>
      )}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Table removeOverflowX={true} className="text-xs">
          <TableHeader className="sticky top-0 z-10 text-muted-foreground shadow-sm">
            <TableRow>
              {isEditMode && <TableHead className="w-8 h-10" />}
              {showTypeColumn && (
                <TableHead className="text-muted-foreground font-semibold pl-3 h-10">
                  Type
                </TableHead>
              )}
              <TableHead
                className={`text-muted-foreground font-semibold h-10 ${!showTypeColumn ? "pl-3" : ""}`}
              >
                Titel
              </TableHead>
              <TableHead className="w-[92px] text-muted-foreground font-semibold h-10">
                Status
              </TableHead>
            </TableRow>
          </TableHeader>
          <Droppable
            droppableId="favorite-documents-items"
            renderClone={(provided, _snapshot, rubric: DraggableRubric) => {
              const item = favoriteDocumentItems[rubric.source.index];
              if (!item) return null;

              const { columnWidths, tableWidth } = measureCloneLayout(
                rubric.source.index
              );
              const { style: dragStyle, ...dragProps } =
                provided.draggableProps;

              return (
                <div
                  ref={provided.innerRef}
                  {...dragProps}
                  style={{
                    ...(dragStyle as React.CSSProperties),
                    width:
                      tableWidth ?? widgetContainerRef.current?.clientWidth,
                    boxSizing: "border-box",
                  }}
                  className="bg-muted/40 shadow-md overflow-hidden"
                >
                  <table className="w-full table-fixed text-xs bg-background">
                    {columnWidths.length > 0 && (
                      <colgroup>
                        {columnWidths.map((width, index) => (
                          <col key={index} style={{ width }} />
                        ))}
                      </colgroup>
                    )}
                    <tbody>
                      {renderCloneDocumentRow(
                        item,
                        provided.dragHandleProps
                      )}
                    </tbody>
                  </table>
                </div>
              );
            }}
          >
            {(provided) => (
              <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                {favoriteDocumentItems.map((item, index) => (
                  <Draggable
                    key={item.id}
                    draggableId={item.id}
                    index={index}
                    isDragDisabled={!isEditMode}
                  >
                    {(draggableProvided, snapshot) =>
                      renderDocumentRow(item, draggableProvided, snapshot)
                    }
                  </Draggable>
                ))}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </Table>
      </DragDropContext>
      <RenameFavoriteDialog
        isOpen={favoriteToRename !== null}
        onClose={() => setFavoriteToRename(null)}
        title={favoriteToRename?.document.title ?? ""}
        type={favoriteToRename?.type ?? "legislation"}
        isPending={
          renameFavoriteLegislation.isPending ||
          renameFavoriteWorkspaceDocument.isPending
        }
        onRename={handleRenameFavorite}
      />
    </div>
  );
};

export default FavoriteDocumentsWidget;
